// QuadOverlayLayer — read-only Frontend-Anzeige rotierter/verzerrter Bild-Overlays (was GroundOverlay nicht kann).
//
// Gegenstück zu GroundOverlayLayer (flach/achsenparallel): hier wird ein Bild aus 4 Eck-Koordinaten (Quad) + einem
// Rotationswinkel per CSS `matrix3d` projiziert — dieselbe Mathematik wie der Editor (tools/quadTransform.js), aber
// READ-ONLY (keine Griffe, nicht klickbar). Manager pro Karte nach Overlay-Id mit Abgleich (retain) → kein Flackern.
//
// Geometrie: `corners` = {nw,ne,se,sw:{lat,lng}} (un-rotiert), `rotation` in Grad (separat, beim Render eingerechnet).
// google wird zur Laufzeit injiziert (OverlayView erst nach API-Load verfügbar), wie im restlichen topoMapCore-Kern.

import { rotatePoint, matrix3dForQuad } from "../tools/quadTransform.js";

export function createQuadOverlayLayerClass(google) {

    const CORNER_KEYS = ['nw', 'ne', 'se', 'sw'];

    // Ein einzelnes read-only Quad-Overlay: Bild per matrix3d aus Eck-LatLngs (+ Rotation um die Mitte).
    class QuadOverlay extends google.maps.OverlayView {
        constructor(map, url, corners, rotation, opacity, zIndex) {
            super();
            this._url = url;
            this._corners = {};
            for (const k of CORNER_KEYS) { this._corners[k] = new google.maps.LatLng(corners[k].lat, corners[k].lng); }
            this._rotation = rotation || 0;
            this._opacity = (opacity == null) ? 1 : opacity;   // 0..1
            this._zIndex = (zIndex == null) ? null : zIndex;   // Layer-Stapelung; null = keine explizite Reihenfolge
            this._div = null;
            this._img = null;
            this.setMap(map);
        }

        onAdd() {
            const div = document.createElement('div');
            div.style.position = 'absolute';
            if (this._zIndex != null) { div.style.zIndex = String(this._zIndex); }

            const img = document.createElement('img');
            img.src = this._url;
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.display = 'block';
            img.draggable = false;
            img.style.transformOrigin = '0 0';

            div.appendChild(img);
            this._div = div;
            this._img = img;
            this.setOpacity(this._opacity);

            // overlayLayer-Pane: sichtbar, aber NICHT klickbar (read-only) — anders als der Editor (overlayMouseTarget).
            this.getPanes().overlayLayer.appendChild(div);
        }

        // Render wie im Editor (draw): Ecken projizieren → um die Mitte rotieren → Div an die Bounding-Box, Bild per matrix3d.
        draw() {
            if (!this._div) { return; }
            const proj = this.getProjection();
            if (!proj) { return; }

            const up = {};
            for (const k of CORNER_KEYS) { const p = proj.fromLatLngToDivPixel(this._corners[k]); up[k] = { x: p.x, y: p.y }; }
            const cx = (up.nw.x + up.ne.x + up.se.x + up.sw.x) / 4;
            const cy = (up.nw.y + up.ne.y + up.se.y + up.sw.y) / 4;
            const center = { x: cx, y: cy };
            const rp = {};
            for (const k of CORNER_KEYS) { rp[k] = rotatePoint(up[k], center, this._rotation || 0); }

            const refW = Math.max(1, Math.max(up.nw.x, up.ne.x, up.se.x, up.sw.x) - Math.min(up.nw.x, up.ne.x, up.se.x, up.sw.x));
            const refH = Math.max(1, Math.max(up.nw.y, up.ne.y, up.se.y, up.sw.y) - Math.min(up.nw.y, up.ne.y, up.se.y, up.sw.y));
            const left = Math.min(rp.nw.x, rp.ne.x, rp.se.x, rp.sw.x);
            const top = Math.min(rp.nw.y, rp.ne.y, rp.se.y, rp.sw.y);
            this._div.style.left = left + 'px';
            this._div.style.top = top + 'px';
            this._div.style.width = refW + 'px';
            this._div.style.height = refH + 'px';

            const rel = {
                nw: { x: rp.nw.x - left, y: rp.nw.y - top },
                ne: { x: rp.ne.x - left, y: rp.ne.y - top },
                sw: { x: rp.sw.x - left, y: rp.sw.y - top },
                se: { x: rp.se.x - left, y: rp.se.y - top }
            };
            this._img.style.transform = matrix3dForQuad(refW, refH, rel);
        }

        setOpacity(opacity) {
            this._opacity = opacity;
            if (this._img) { this._img.style.opacity = String(Math.max(0, Math.min(1, opacity))); }
        }

        setZIndex(zIndex) {
            this._zIndex = zIndex;
            if (this._div && zIndex != null) { this._div.style.zIndex = String(zIndex); }
        }

        onRemove() {
            if (this._div && this._div.parentNode) { this._div.parentNode.removeChild(this._div); }
            this._div = null;
            this._img = null;
        }

        remove() { this.setMap(null); }
    }

    // Manager: Quad-Overlays pro Karte nach Id, mit retain-Abgleich gegen die sichtbaren Ids (analog GroundOverlayLayer).
    return class QuadOverlayLayer {
        constructor(map) {
            this._map = map;
            this._items = new Map();   // String(id) -> QuadOverlay
        }

        // Legt das Overlay an (oder aktualisiert nur die Opacity, falls schon vorhanden). corners = {nw,ne,se,sw:{lat,lng}},
        // rotation in Grad, opacity 0..1. Geometrie ändert sich im Frontend nicht zur Laufzeit → bei Änderung remove + ensure.
        ensure(id, { url, corners, rotation = 0, opacity = 1, zIndex = null }) {
            const key = String(id);
            let q = this._items.get(key);
            if (!q) {
                q = new QuadOverlay(this._map, url, corners, rotation, opacity, zIndex);
                this._items.set(key, q);
            } else {
                q.setOpacity(opacity);
                if (zIndex != null) { q.setZIndex(zIndex); }
            }
            return q;
        }

        setOpacity(id, opacity) {
            const q = this._items.get(String(id));
            if (q) { q.setOpacity(opacity); }
        }

        remove(id) {
            const key = String(id);
            const q = this._items.get(key);
            if (q) {
                q.remove();
                this._items.delete(key);
            }
        }

        // Behält nur die Overlays mit den übergebenen Ids; entfernt alle anderen (Abgleich nach Sichtbarkeit).
        retain(ids) {
            const keep = new Set((ids || []).map(String));
            for (const key of [...this._items.keys()]) {
                if (!keep.has(key)) { this.remove(key); }
            }
        }

        clear() {
            this.retain([]);
        }
    };
}
